import { APIProvider, Map, Circle, useMap } from '@vis.gl/react-google-maps';
import { useEffect } from 'react';
import { primary, info, warning, success, error, neutral } from '@/theme';

interface SectorMapItem {
  id: string;
  label: string;
  address?: string;
  latitude: number;
  longitude: number;
  rangeKm: number;
  society: { id: string; name: string; logo?: string; type: string };
}

interface SectorMapProps {
  sectors: SectorMapItem[];
  selectedSectorId: string | null;
  onSelectSector: (id: string) => void;
  center?: { lat: number; lng: number };
}

function MapController({ center }: { center?: { lat: number; lng: number } }) {
  const map = useMap();

  useEffect(() => {
    if (map && center) {
      map.panTo(center);
      map.setZoom(12);
    }
  }, [map, center]);

  return null;
}

const societyTypeColors: Record<string, string> = {
  INSTALLER: info[400],
  DISTRIBUTOR: warning[400],
  SECURITY: success[400],
};

export function SectorMap({ sectors, selectedSectorId, onSelectSector, center }: SectorMapProps) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '';

  if (!apiKey) {
    return (
      <div
        style={{
          height: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: neutral[100],
          borderRadius: 8,
        }}
      >
        Clé API Google Maps manquante (VITE_GOOGLE_MAPS_API_KEY)
      </div>
    );
  }

  return (
    <APIProvider apiKey={apiKey}>
      <Map
        defaultCenter={{ lat: 46.227638, lng: 2.213749 }}
        defaultZoom={5}
        gestureHandling="greedy"
        disableDefaultUI={false}
        mapId="wisecure-sectors"
        style={{ height: 500, width: '100%', borderRadius: 8 }}
      >
        <MapController center={center} />
        {sectors.map((sector) => {
          const isSelected = sector.id === selectedSectorId;
          const color = societyTypeColors[sector.society.type] ?? primary[400];
          return (
            <Circle
              key={sector.id}
              center={{ lat: sector.latitude, lng: sector.longitude }}
              radius={sector.rangeKm * 1000}
              fillColor={isSelected ? error[400] : color}
              fillOpacity={isSelected ? 0.3 : 0.15}
              strokeColor={isSelected ? error[400] : color}
              strokeWeight={isSelected ? 3 : 1}
              onClick={() => onSelectSector(sector.id)}
            />
          );
        })}
      </Map>
    </APIProvider>
  );
}
